import { clientPortalAssistantApi } from '@/lib/api/client-portal-assistant.api'
import { useAuth } from '@/store/auth.store'
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

/**
 * Portal Benny query keys. Every key carries the signed-in user id as well as
 * the client entity: the server already returns only this user's
 * conversations, and keying by user keeps a shared browser tab from ever
 * painting another person's cached threads after a sign-in switch.
 */
export const clientPortalAssistantKeys = {
  all: (entityId: string, userId: string) =>
    ['client-portal-assistant', entityId, userId] as const,
  capabilities: (entityId: string, userId: string) =>
    [
      ...clientPortalAssistantKeys.all(entityId, userId),
      'capabilities',
    ] as const,
  sessions: (entityId: string, userId: string) =>
    [...clientPortalAssistantKeys.all(entityId, userId), 'sessions'] as const,
  messages: (entityId: string, userId: string, sessionId: string) =>
    [
      ...clientPortalAssistantKeys.all(entityId, userId),
      'messages',
      sessionId,
    ] as const,
}

/** The signed-in user every portal Benny query belongs to. */
export function useClientPortalAssistantOwner() {
  return useAuth(state => state.user?.id) ?? null
}

export function useClientPortalAssistantCapabilities(
  entityId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  const userId = useClientPortalAssistantOwner()
  return useQuery({
    queryKey: clientPortalAssistantKeys.capabilities(
      entityId ?? '',
      userId ?? '',
    ),
    queryFn: () => clientPortalAssistantApi.getCapabilities(entityId!),
    enabled: options.enabled !== false && !!entityId && !!userId,
    // A 403 means the portal switch or the org's AI flag is off: not worth
    // retrying, and the pill simply stays hidden.
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
}

export function useClientPortalAssistantSessions(
  entityId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  const userId = useClientPortalAssistantOwner()
  return useQuery({
    queryKey: clientPortalAssistantKeys.sessions(entityId ?? '', userId ?? ''),
    queryFn: () => clientPortalAssistantApi.listSessions(entityId!),
    enabled: options.enabled !== false && !!entityId && !!userId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  })
}

/**
 * One definition for the thread query, so the chat can `fetchInfiniteQuery`
 * the same key after a stream and only drop its in-progress bubble once the
 * persisted reply is in the cache (a brand-new conversation's query is not
 * mounted yet when its first turn ends).
 */
export function clientPortalAssistantMessagesOptions(
  entityId: string,
  userId: string,
  sessionId: string,
) {
  return infiniteQueryOptions({
    queryKey: clientPortalAssistantKeys.messages(entityId, userId, sessionId),
    queryFn: ({ pageParam }) =>
      clientPortalAssistantApi.getMessages(entityId, sessionId, {
        cursor: pageParam,
        limit: 20,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage =>
      lastPage.hasMore ? (lastPage.nextCursor ?? undefined) : undefined,
    staleTime: 60 * 1000,
  })
}

export function useClientPortalAssistantMessages(
  entityId: string | undefined,
  sessionId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  const userId = useClientPortalAssistantOwner()
  return useInfiniteQuery({
    ...clientPortalAssistantMessagesOptions(
      entityId ?? '',
      userId ?? '',
      sessionId ?? '',
    ),
    enabled: options.enabled !== false && !!entityId && !!sessionId && !!userId,
    refetchOnWindowFocus: false,
  })
}

export function useRenameClientPortalAssistantSession(
  entityId: string | undefined,
) {
  const userId = useClientPortalAssistantOwner()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sessionId, title }: { sessionId: string; title: string }) =>
      clientPortalAssistantApi.renameSession(entityId!, sessionId, title),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: clientPortalAssistantKeys.sessions(
          entityId ?? '',
          userId ?? '',
        ),
      }),
  })
}

export function useDeleteClientPortalAssistantSession(
  entityId: string | undefined,
) {
  const userId = useClientPortalAssistantOwner()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) =>
      clientPortalAssistantApi.deleteSession(entityId!, sessionId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: clientPortalAssistantKeys.sessions(
          entityId ?? '',
          userId ?? '',
        ),
      }),
  })
}
